import { useEffect, useMemo, useRef } from "react";
import { useApolloClient } from "@apollo/client/react";

import { reloadFeatureFlags } from "#app/hooks/use-feature-flags";
import { useAuth } from "#app/lib/auth-context";
import {
  mutationsInFlight,
  useAppData,
  useWorkspace,
  WorkspaceChangedDocument,
} from "#app/lib/data";
import {
  defineSubscription,
  useRegisterSubscriptions,
} from "#app/lib/subscriptions/subscription-provider";

// The queries that read each part of the workspace, by operation name. A
// `workspaceChanged` event refetches the ACTIVE ones only — a screen nobody
// has open costs nothing, and reads fresh when it next mounts.
const SCOPE_QUERIES: Record<string, readonly string[]> = {
  workspace: [
    "GetWorkspaces",
    "GetWorkspaceAdvancedPermissions",
    "GetWorkspaceAgeSettings",
    "GetWorkspaceIssueSourceSettings",
  ],
  workflows: [
    "WorkflowsListData",
    "GetWorkflow",
    "WorkflowStagesData",
    "StagesByWorkflow",
    "GetStagesForWorkflow",
    "StageCycleOverrideCount",
    "CreateIssueData",
  ],
  teams: ["TeamsListData", "GetTeam", "GetTeamMembers", "TeamWorkflowRoster"],
  members: [
    "MembersListData",
    "MembersGroupData",
    "GetMember",
    "MemberFacetCounts",
    "GetWorkspaceUsers",
    "OrgChartUsers",
  ],
  roles: [
    "PermissionMatrix",
    "RoleInviteGrantMatrix",
    "MyInvitableRoles",
    "MembersListData",
    "GetMember",
  ],
  sharing: ["WorkflowAccessSummary", "WorkflowTeamAccessMembers", "UserAccessList"],
  itemSharing: [
    "ItemAccessSummary",
    "ItemTeamAccessMembers",
    "EntityViewAccessSummary",
    "EntityViewTeamAccessMembers",
    "UserAccessList",
    "EntityViews",
    "EntityViewsByType",
  ],
  issueTypes: [
    "IssueTypeList",
    "GetIssueTypes",
    "GetIssueType",
    "CreateIssueData",
    "IssueViewData",
  ],
  customFields: [
    "CustomFieldsListData",
    "GetIssueTypes",
    "GetIssueType",
    "CreateIssueData",
    "IssueViewData",
  ],
  reasons: ["GetReasons"],
  issueSources: [
    "GetIssueSources",
    "GetActiveIssueSources",
    "GetWorkspaceIssueSourceSettings",
  ],
  jobTitles: ["JobTitles"],
  views: ["EntityViews", "EntityViewsByType"],
  automations: ["GetAutomations", "GetAutomation"],
  integrations: ["IntegrationCatalog", "GetApiKeys", "GetWebhooks"],
  preferences: [
    "GetMyPreferences",
    "GetUserSettings",
    "GetSetting",
    "GetFavoriteIssues",
    "GetDiscovery",
    "GetDiscoveries",
  ],
  featureFlags: [],
};

// Scopes that AppProvider's WorkspaceData snapshot reads (workflows, stages,
// teams, members, the viewer's roles, which workflows sharing lets them see;
// project and view sharing, `itemSharing`, are not in it). The
// snapshot sits in reducer state, so a query refetch never reaches it — only
// its own refetch does.
const APP_DATA_SCOPES = new Set([
  "workspace",
  "workflows",
  "teams",
  "members",
  "roles",
  "sharing",
]);

// Events arrive in bursts (a bulk share writes one row per member), so they
// collect for this long and refetch once.
const FLUSH_DELAY_MS = 250;

// A flag change reaches every connected client in every workspace at once.
// Each one waits a random part of this window before it asks the flags
// service again, so the requests spread out instead of arriving together.
const FLAG_RELOAD_JITTER_MS = 5000;

/**
 * Put on a query's `context` when the query grows its result with
 * `fetchMore`. A plain refetch re-runs only the first page and drops the rows
 * loaded after it, so the live sync calls this re-read instead.
 */
export type LiveRefetchContext = { liveRefetch?: () => Promise<unknown> };

/**
 * Keeps every settings-level read live: subscribes to `workspaceChanged` and
 * refetches what the changed scope reads — for every viewer, the actor's own
 * tab included. Mount once, in the workspace layout.
 */
export function useLiveWorkspaceSync(): void {
  const client = useApolloClient();
  const { refetch: refetchAppData } = useAppData();
  const { getAccessToken } = useAuth();
  const { activeWorkspace } = useWorkspace();
  const workspaceId = activeWorkspace?.workspaceId ?? null;

  const pending = useRef(new Set<string>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flagTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The flush reads the latest values through a ref, so the subscription
  // descriptor below stays one module-stable registration.
  const flushRef = useRef<() => void>(() => {});
  flushRef.current = () => {
    timer.current = null;
    // A refetch that lands while this tab's own write is in flight reads
    // the value from before it and overwrites the optimistic one. Wait for
    // the write: its own event follows and the refetch then reads it.
    if (mutationsInFlight() > 0) {
      timer.current = setTimeout(() => flushRef.current(), FLUSH_DELAY_MS);
      return;
    }
    const scopes = [...pending.current];
    pending.current.clear();
    if (scopes.length === 0) return;

    // A scope this build does not know (a newer server) refetches every
    // active query rather than miss the change.
    const unknown = scopes.some((s) => !(s in SCOPE_QUERIES));
    if (unknown || scopes.some((s) => APP_DATA_SCOPES.has(s))) {
      void refetchAppData();
    }
    if (scopes.includes("featureFlags") && !flagTimer.current) {
      flagTimer.current = setTimeout(() => {
        flagTimer.current = null;
        void reloadFeatureFlags(workspaceId, getAccessToken).catch((err: unknown) =>
          console.warn("[live-sync] feature flag reload failed:", err),
        );
      }, Math.random() * FLAG_RELOAD_JITTER_MS);
    }
    const names = new Set(scopes.flatMap((s) => SCOPE_QUERIES[s] ?? []));
    for (const query of client.getObservableQueries("active")) {
      if (unknown || (query.queryName && names.has(query.queryName))) {
        const own = (query.options.context as LiveRefetchContext | undefined)
          ?.liveRefetch;
        void (own ? own() : query.refetch()).catch(() => {
          // The query's own error state reports a failure; nothing to add.
        });
      }
    }
  };

  // The layout stays mounted across a workspace switch. Drop what the old
  // workspace queued, or its flush would refetch into the new one while the
  // switch is still loading it.
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      if (flagTimer.current) clearTimeout(flagTimer.current);
      timer.current = null;
      flagTimer.current = null;
      pending.current.clear();
    },
    [workspaceId],
  );

  const requests = useMemo(
    () => [
      defineSubscription({
        document: WorkspaceChangedDocument,
        onData: (_client, data) => {
          pending.current.add(data.workspaceChanged.scope);
          if (timer.current) return;
          timer.current = setTimeout(() => flushRef.current(), FLUSH_DELAY_MS);
        },
      }),
    ],
    [],
  );
  useRegisterSubscriptions(workspaceId ? requests : NO_REQUESTS);
}

const NO_REQUESTS: never[] = [];
